import { message, Modal, Upload } from "antd";
import Button from "Components/Button";
import Input from "Components/Input";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import Textarea from "Components/Textarea";
import { useCreateCategoryMutation } from "hooks/CategoryManagement/useCreateCategoryMutation";
import React, { ChangeEvent, useState } from "react";
import { queryClient } from "services/queryClient";
import { useIconUploader } from "hooks/UploadIcon";

type Props = {
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
};

const initialState = {
  name: "",
  description: "",
  iconLink: "",
};

const SearchBox: React.FC<Props> = ({ search, setSearch }) => {
  const [payload, setPayload] = useState(initialState);
  const [addCategoryModal, setAddCategoryModal] = useState(false);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setPayload((prev) => ({ ...prev, [name]: value }));
  };

  const handleModalClose = () => {
    setAddCategoryModal(false);
    setPayload(initialState);
  };

  const { mutateAsync, isPending } = useCreateCategoryMutation();
  const { mutateAsync: uploadImage, isPending: isUploadPending } =
    useIconUploader({ payload, setPayload });

  // const [images, setImages] = useState<string>("");

  const handleUpload = async (info: any) => {
    const file = info.file.originFileObj;

    if (file) {
      const reader = new FileReader();

      reader.onload = async () => {
        const imageBase64 = reader.result as string;
        // setImages(imageBase64);

        try {
          await uploadImage(imageBase64);
        } catch (error) {
          console.error("Image upload failed:", error);
          message.error("Error uploading file");
        }
      };

      reader.readAsDataURL(file);
    }
  };

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button">
      {isUploadPending ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>Upload Icon</div>
    </button>
  );

  return (
    <div className="flex gap-[10px]">
      <span className="relative">
        <SearchIcon />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-[10px] border-[1px] border-[#ababab] text-[13px] p-[13px] pl-[35px]"
          placeholder="Search"
        />
      </span>
      <button
        onClick={() => setAddCategoryModal(!addCategoryModal)}
        className="text-white bg-orange py-[13px] height-auto p-3 rounded-[10px] text-[14px] flex gap-[10px] items-center"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className="size-6"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 4.5v15m7.5-7.5h-15"
          />
        </svg>
        <span>Add New Category</span>
      </button>
      <Modal
        title="Create New Category"
        open={addCategoryModal}
        onCancel={handleModalClose}
        footer={null}
      >
        <Input
          type="text"
          name="name"
          label="Name"
          value={payload.name}
          placeholder="Enter Category Name"
          onChange={handleInputChange}
        />
        <Textarea
          label="Description"
          name={"description"}
          value={payload?.description}
          placeholder={""}
          onChange={handleInputChange}
        />

        <label
          htmlFor="iconLink"
          className="block mb-2 text-grey text-[16px] manrope capitalize"
        >
          Upload Icon
        </label>

        <div className="mb-3 bg-white text-[16px] h-[150px] resize-none w-full  ease-in-out duration-300 focus:border-orange rounded-md">
          <Upload
            name="iconLink"
            listType="picture-card"
            className="avatar-uploader"
            showUploadList={false}
            onChange={handleUpload}
          >
            {payload?.iconLink ? (
              <img
                src={payload?.iconLink}
                alt="iconLink"
                style={{ width: "100%" }}
              />
            ) : (
              uploadButton
            )}
          </Upload>
        </div>
        <Button
          className="text-white p-[12px] w-full font-bold rounded-md ease-in-out duration-300 bg-brown"
          disabled={!payload.name || !payload.description || !payload?.iconLink}
          isLoading={isPending}
          label={"Proceed"}
          onClick={() => {
            mutateAsync(payload, {
              onSuccess: (data: any) => {
                message.success(data?.message);
                handleModalClose();
                queryClient.invalidateQueries({
                  queryKey: ["useGetCategory"],
                });
              },
            });
          }}
        />
      </Modal>
    </div>
  );
};

export default SearchBox;

const SearchIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className="size-4 absolute top-[18px] left-[15px] text-[#ababab]"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
      />
    </svg>
  );
};
